import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { Alert, Typography } from '@mui/material';
import {
  MaterialReactTable,
  MRT_ColumnDef,
  useMaterialReactTable,
} from 'material-react-table';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';

import { Main } from '../../components';
import { ADMIN_CONTENT_REPORTS_QUERY } from '../../gql';
import { useMaterialReactTableLocalization } from '../../i18n';

interface ContentReportRow {
  authorUserId?: string | null;
  contentSnapshot: string;
  createdAt: string;
  details?: string | null;
  entityId: string;
  entityType: string;
  id: string;
  reason: string;
  reporterUserId?: string | null;
  status: string;
}

const gridStyles = { display: 'grid', marginBottom: 6 };
const snapshotStyles = {
  display: 'block',
  maxWidth: 360,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const emptyReports: ContentReportRow[] = [];

function createColumns(intl: IntlShape): MRT_ColumnDef<ContentReportRow>[] {
  return [
    {
      accessorKey: 'id',
      header: intl.formatMessage({
        id: 'admin.reports.id',
        defaultMessage: 'Report ID',
      }),
      size: 190,
    },
    {
      accessorFn: (row) => new Date(row.createdAt),
      header: intl.formatMessage({
        id: 'admin.reports.created',
        defaultMessage: 'Reported',
      }),
      Cell: ({ cell }) =>
        intl.formatDate(cell.getValue<Date>(), {
          dateStyle: 'medium',
          timeStyle: 'short',
        }),
      size: 170,
    },
    {
      accessorKey: 'status',
      header: intl.formatMessage({
        id: 'admin.reports.status',
        defaultMessage: 'Status',
      }),
      size: 100,
    },
    {
      accessorKey: 'entityType',
      header: intl.formatMessage({
        id: 'admin.reports.entityType',
        defaultMessage: 'Content type',
      }),
      size: 130,
    },
    {
      accessorKey: 'entityId',
      header: intl.formatMessage({
        id: 'admin.reports.entityId',
        defaultMessage: 'Content ID',
      }),
      size: 190,
    },
    {
      accessorKey: 'reason',
      header: intl.formatMessage({
        id: 'admin.reports.reason',
        defaultMessage: 'Reason',
      }),
      size: 190,
    },
    {
      accessorKey: 'reporterUserId',
      header: intl.formatMessage({
        id: 'admin.reports.reporter',
        defaultMessage: 'Reporter ID',
      }),
      Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      size: 190,
    },
    {
      accessorKey: 'authorUserId',
      header: intl.formatMessage({
        id: 'admin.reports.author',
        defaultMessage: 'Author ID',
      }),
      Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      size: 190,
    },
    {
      accessorKey: 'details',
      header: intl.formatMessage({
        id: 'admin.reports.details',
        defaultMessage: 'Details',
      }),
      Cell: ({ cell }) => cell.getValue<string | null>() ?? '—',
      size: 260,
    },
    {
      accessorKey: 'contentSnapshot',
      header: intl.formatMessage({
        id: 'admin.reports.snapshot',
        defaultMessage: 'Content snapshot',
      }),
      Cell: ({ cell }) => {
        const snapshot = cell.getValue<string>();

        return (
          <Typography title={snapshot} variant="body2" sx={snapshotStyles}>
            {snapshot}
          </Typography>
        );
      },
      size: 360,
    },
  ];
}

export const AdminContentReports = React.memo(function AdminContentReports() {
  const intl = useIntl();
  const localization = useMaterialReactTableLocalization();
  const { data, error, loading } = useQuery<{
    adminContentReports: ContentReportRow[];
  }>(ADMIN_CONTENT_REPORTS_QUERY, {
    fetchPolicy: 'cache-and-network',
  });
  const rows = data?.adminContentReports ?? emptyReports;
  const columns = useMemo(() => createColumns(intl), [intl]);

  const table = useMaterialReactTable({
    columns,
    data: rows,
    localization,
    enableColumnActions: false,
    enableColumnFilters: false,
    enableToolbarInternalActions: false,
    initialState: {
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    muiPaginationProps: { showRowsPerPage: false },
    muiTablePaperProps: { sx: gridStyles },
    state: { isLoading: loading },
  });

  return (
    <Main>
      {error && (
        <Alert severity="error">
          <FormattedMessage
            id="admin.reports.loadError"
            defaultMessage="Unable to load content reports."
          />
        </Alert>
      )}
      <MaterialReactTable table={table} />
    </Main>
  );
});
